/* eslint-disable max-len */
import { useField, useFormikContext } from 'formik';
import { ReactElement, useState } from 'react';
import { useSealOfBiliteracyContext } from 'src/contexts/SealOfBiliteracyContext';
import { ButtonSize } from 'src/types/ButtonSize';
import { PanelType } from 'src/types/PanelType';
import { SealOfBiliteracyModel } from 'src/types/SealOfBiliteracyModel';
import { StudentModel } from 'src/types/StudentModel';
import { isValidCode } from '../../../services/base.service';
import { getStudentByMSISId } from '../../../services/sealOfBiliteracy.service';
import ActionButton from '../../ActionButton/ActionButton';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

interface SOBStudentPanelProps {
  dataEntryMode?: 'new' | 'edit';
}

const SOBStudentPanel = ({
  dataEntryMode = 'new',
}: SOBStudentPanelProps): ReactElement => {
  const { values, setFieldValue, setFieldTouched, validateField } =
    useFormikContext<SealOfBiliteracyModel>();
  const { dynamicValidationSchema } = useSealOfBiliteracyContext();

  const [, msisIdMetaProps] = useField('studentIdentifier');
  const getIsMsisIDValid = (): boolean => !msisIdMetaProps?.error;

  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [hasSearchMatch, setHasSearchMatch] = useState(
    dataEntryMode === 'edit'
  );

  const searchDisabled =
    !getIsMsisIDValid() ||
    searching ||
    hasSearchMatch ||
    dataEntryMode === 'edit';

  const lookupSearchMessage = (status: string): string => {
    if (status === '404') {
      return 'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.';
    } else if (status === '403') {
      return 'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again.';
    } else if (status === '405') {
      return 'The MSIS ID for this student already has a removal request. Please check the MSIS ID and try again.';
    } else if (status === '409') {
      return 'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again.';
    } else if (status === '422') {
      return 'This student is not in an active cohort. Please check the MSIS ID and try again.';
    } else {
      return 'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.';
    }
  };

  const handleMSISSearch = async (): Promise<void> => {
    const setUserFields = (data: StudentModel): void => {
      setFieldValue('studentFirstName', data.firstName, true);
      setFieldValue('studentLastOrSurname', data.lastName, true);
      setFieldValue('studentMiddleName', data.middleName || '', true);
      setFieldValue('districtName', data.districtName || '', true);
      setFieldValue('schoolName', data.schoolName || '', true);
      setFieldValue('leaIdentifier', data.districtId || '', true);
      setFieldValue('schoolIdentifier', data.schoolId || '', true);
    };

    setSearching(true);
    setSearchMessage('');

    try {
      const effectiveSchoolYear = values.schoolYear || '';
      const response = await getStudentByMSISId(values.studentIdentifier, effectiveSchoolYear);
      if (isValidCode(response.status)) {
        setUserFields(response.data);
        setSearchMessage('');
        setHasSearchMatch(true);
      } else {
        const errorMessage = lookupSearchMessage(response.status.toString());
        setSearchMessage(errorMessage);
      }
    } catch (error) {
      setSearchMessage('An error occurred while searching. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleMsisValueChange = (): Promise<void> => {
    setFieldValue('studentFirstName', '', true);
    setFieldValue('studentLastOrSurname', '', true);
    setFieldValue('studentMiddleName', '', true);
    setFieldValue('districtName', '', true);
    setFieldValue('schoolName', '', true);
    setFieldTouched('studentFirstName', false);
    setFieldTouched('studentLastOrSurname', false);
    setFieldTouched('studentMiddleName', false);
    setFieldTouched('districtName', false);
    setFieldTouched('schoolName', false);

    setSearchMessage('');
    if (hasSearchMatch) {
      setHasSearchMatch(false);
    }

    setTimeout(() => {
      validateField('studentIdentifier');
    }, 0);

    return Promise.resolve();
  };

  const getDistrictDisplayValue = (): string => {
    if (values.districtName && values.leaIdentifier) {
      const identifierPattern = new RegExp(`\\(${values.leaIdentifier}\\)$`);
      if (identifierPattern.test(values.districtName)) {
        return values.districtName;
      }
      return `${values.districtName} (${values.leaIdentifier})`;
    }
    return values.districtName || '';
  };

  const getSchoolDisplayValue = (): string => {
    if (values.schoolName && values.schoolIdentifier) {
      const identifierPattern = new RegExp(`\\(${values.schoolIdentifier}\\)$`);
      if (identifierPattern.test(values.schoolName)) {
        return values.schoolName;
      }
      return `${values.schoolName} (${values.schoolIdentifier})`;
    }
    return values.schoolName || '';
  };

  return (
    <div
      data-testid="seal-of-biliteracy-input-child"
      className="data-input--seal-of-biliteracy--input"
    >
      <Panel panelType={PanelType.INFO} heading="Student Information">
        <div className="field-row field-row-msis-id">
          <div>
            <FormTextbox
              displayName="MSIS ID"
              field="studentIdentifier"
              validationSchema={dynamicValidationSchema}
              disabled={dataEntryMode === 'edit'}
              onChangeEvent={handleMsisValueChange}
            />
          </div>
          <div className="search-button">
            <ActionButton
              classes="button--secondary"
              size={ButtonSize.SMALL}
              disabled={searchDisabled}
              dataTestId="student-search-button"
              tooltipText="Click to search for student"
              onClick={handleMSISSearch}
              loading={searching}
            >
              Search
            </ActionButton>
          </div>
          <div className="search-message" data-testid="search-error-message">
            {searchMessage}
          </div>
        </div>

        <div className="field-row field-row-1-1-1">
          <FormTextbox
            displayName="Last Name"
            field="studentLastOrSurname"
            validationSchema={dynamicValidationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="First Name"
            field="studentFirstName"
            validationSchema={dynamicValidationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="Middle Name"
            field="studentMiddleName"
            validationSchema={dynamicValidationSchema}
            disabled={true}
          />
        </div>
      </Panel>

      <Panel panelType={PanelType.INFO} heading="LEA & School Information">
        <div className="field-row field-row-1-1-1">
          <div
            className="field-container disabled"
            data-testid="form-textbox-districtName"
          >
            <label
              className="field-label"
              data-testid="display-name-label"
              htmlFor="districtName"
            >
              LEA
            </label>
            <input
              className="data-input"
              id="districtName"
              name="districtName"
              type="text"
              disabled
              value={getDistrictDisplayValue()}
              data-testid="form-textbox-districtName-input"
            />
          </div>
          <div
            className="field-container disabled"
            data-testid="form-textbox-schoolName"
          >
            <label
              className="field-label"
              data-testid="display-name-label"
              htmlFor="schoolName"
            >
              School
            </label>
            <input
              className="data-input"
              id="schoolName"
              name="schoolName"
              type="text"
              disabled
              value={getSchoolDisplayValue()}
              data-testid="form-textbox-schoolName-input"
            />
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default SOBStudentPanel;
